import { BaseSyntheticEvent, useState } from "react";
import { IMaskInput } from "react-imask";
import validator from "validator";

import "./Input.scss";

import { Placeholder } from "../../Components/Typography";
import { IconClose, IconHelp } from "../Icons";
import { isEmpty } from "../../utils/isEmpty";

interface Props {
  title?: string;
  placeholder?: string;
  multiline?: boolean;
  className?: string;
  helpContent?: any;
  type?: string;
  onChange?: any;
  onBlur?: any;
  value?: string | number;
  error?: string;
  mask?: any;
  disabled?: boolean;
  min?: number;
  max?: number;
  length?: number;
  phoneNumber?: boolean;
}

export const Input = ({
  title,
  placeholder,
  multiline,
  className,
  helpContent,
  type,
  onChange,
  onBlur,
  value,
  error,
  mask,
  disabled,
  min,
  max,
  length,
  phoneNumber,
}: Props) => {
  const [helpFadeOut, setHelpFadeOut] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const onClickHelp = () => {
    setHelpOpen(true);
  };

  const onClickHelpClose = () => {
    setHelpFadeOut(true);

    setTimeout(() => {
      setHelpOpen(false);
      setHelpFadeOut(false);
    }, 500);
  };

  const sanitizeText = (text: string) =>
    validator.blacklist(text, "\\[\\]\\!\\*\\;\\<\\>\\\\{\\}\\|\\&");

  const onChangeValidate = (text: string) => {
    const sanitizedText = sanitizeText(text);

    if (length && sanitizedText.length > length) return;

    onChange(sanitizedText);
  };

  const onChangeInput = (e: BaseSyntheticEvent) => {
    if (onChange) {
      onChangeValidate(e?.target?.value);
    }
  };

  const onChangeMaskInput = (value: any, maskRef: any, e: any) => {
    if (onChange && !isEmpty(e?.target?.value)) {
      onChangeValidate(e?.target?.value);
    }
  };

  const onChangeNumber = (e: BaseSyntheticEvent) => {
    if(/^(?=.*\d)[\d ]+$/.test(e?.target?.value) || e?.target?.value === '') {
      onChangeInput(e)
    }
  }

  const onBlurInput = (e: BaseSyntheticEvent) => {
    if (onBlur) {
      onBlur(sanitizeText(e?.target?.value));
    }
  };

  const getInputType = () => {
    if (multiline) {
      return (
        <textarea
          disabled={disabled}
          className="textbox textarea"
          placeholder={placeholder}
          style={{ height: "140px" }}
          onChange={onChangeInput}
          onBlur={onBlurInput}
          value={value}
        />
      );
    }

    if (mask) {
      return (
        <IMaskInput
          unmask
          disabled={disabled}
          className={`textbox ${error ? "input-error" : ""}`}
          mask={mask}
          radix="."
          value={value ? value.toString() : ""}
          onAccept={onChangeMaskInput}
          placeholder={placeholder}
          min={min}
          max={max}
          maxLength={length}
        />
      );
    }

    return phoneNumber ? (
      <div className="flex-row" style={{ width: "100%" }}>
        <input
          style={{ width: "25px" }}
          disabled
          className={`textbox ${error ? "input-error" : ""}`}
          value={"+54"}
        />
        <input
          disabled={disabled}
          className={`textbox ${error ? "input-error" : ""}`}
          placeholder={placeholder}
          type={type}
          onChange={onChangeNumber}
          onBlur={onBlurInput}
          value={value}
          maxLength={length}
          style={{ width: "100%" }}
        />
      </div>
    ) : (
      <input
        disabled={disabled}
        className={`textbox ${error ? "input-error" : ""}`}
        placeholder={placeholder}
        type={type}
        onChange={onChangeInput}
        onBlur={onBlurInput}
        value={value}
        maxLength={length}
      />
    );
  };

  return (
    <div className={`input ${className ? className : ""}`}>
      <div className="input-title">
        <div>
          {title && <Placeholder>{title}</Placeholder>}
          {helpContent && (
            <IconHelp className="help-icon" onClick={onClickHelp} />
          )}
        </div>
        {error && <span className="error-text">{error}</span>}
      </div>

      {getInputType()}

      {helpContent && helpOpen && (
        <div
          className={`help-popover animate__animated animate__faster animate__fadeIn ${
            helpFadeOut && "animate__fadeOut"
          }`}
        >
          <IconClose className="close-icon" onClick={onClickHelpClose} />

          <div className="content">{helpContent}</div>
        </div>
      )}
    </div>
  );
};
